import { toast } from 'sonner';

import { useSendMessageMutation } from '@/modules/messaging/mutations/useSendMessageMutation';
import { messagingQueryKeys } from '@/modules/messaging/queries/queryKeys';
import { useThreadMessagesQuery } from '@/modules/messaging/queries/useThreadMessagesQuery';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { invalidateQuery } from '@/utils/queries';

import { MessageComposer } from './MessageComposer';
import { MessageList } from './MessageList';

const MESSAGE_STATUSES = [
  'Failed' as const,
  'Pending' as const,
  'Read' as const,
  'Sent' as const,
];

interface MessageThreadProps {
  threadId: string;
}

export const MessageThread = ({ threadId }: MessageThreadProps) => {
  const threadMessagesQuery = useThreadMessagesQuery({
    threadId,
    statuses: MESSAGE_STATUSES,
  });
  const sendMessageMutation = useSendMessageMutation();

  const handleSendMessage = (content: string) => {
    sendMessageMutation.mutate(
      {
        threadId,
        message: {
          content,
          subject: null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Message has been sent');
        },
        onError: (error) => {
          toast.error(
            `Message has not been sent: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const invalidateThreadMessages = invalidateQuery(
    messagingQueryKeys.threadMessages(threadId, MESSAGE_STATUSES),
  );

  return (
    <>
      <MessageList
        threadId={threadId}
        messages={threadMessagesQuery.data}
        isLoading={threadMessagesQuery.isLoading}
        onMessageUpdated={invalidateThreadMessages}
        onMessageDeleted={invalidateThreadMessages}
      />
      <MessageComposer
        isSending={sendMessageMutation.isPending}
        onSend={handleSendMessage}
      />
    </>
  );
};
